import {useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import {useNavigate} from 'react-router-dom'
import {HappyStateCard} from '@/components/worklist-row'
import {RegimeDriftBanner} from '@/components/regime-drift-banner'
import {EstadoDeArranque} from '@/components/resumen-arranque'
import {
  ColaTabla,
  CountUp,
  DonutChart,
  Eyebrow,
  RadialGauge,
  TileCard,
  TileVacio,
  type FilaCola,
} from '@/components/resumen-piezas'
import {Skeleton} from '@/components/ui/skeleton'
import {useChartTokens} from '@/components/ui/apex-chart'
import {useActions} from '@/hooks/sgsst/plan/use-actions'
import {useEvidence} from '@/hooks/sgsst/evidencias/use-evidence'
import {toSoporteDesignations, useDesignations} from '@/hooks/sgsst/evaluacion/use-designations'
import {useSolicitudes} from '@/hooks/sgsst/evidencias/use-solicitudes'
import {usePlanTrabajo, type PlanTrabajo} from '@/hooks/sgsst/plan/use-plan-trabajo'
import type {Dashboard} from '@/hooks/sgsst/resumen/use-dashboard'
import type {Ruta} from '@/hooks/sgsst/resumen/use-ruta'
import type {SgsstState} from '@/hooks/sgsst/evaluacion/use-sgsst'
import {readiness} from '@/lib/sgsst-readiness'
import {formatShortDate} from '@/lib/risk'

// Más de cinco ya es la lista del plan, a la que enlaza la última fila.
const MAX_ACCIONES_EN_COLA = 5

const DIAS_ESTA_SEMANA = 7

const ALTO_GRAFICA = 148

// Clases literales para que Tailwind las vea al compilar.
const COLUMNAS_ARRANQUE: Record<number, string> = {
  2: 'sm:col-span-2',
  3: 'sm:col-span-3',
}

function sumarDias(isoDate: string, dias: number): string {
  const [y, m, d] = isoDate.split('-').map(Number)
  const fecha = new Date(y, m - 1, d + dias)
  return new Intl.DateTimeFormat('en-CA').format(fecha)
}

/** Todo «vencido» se compara contra `dashboard.hoy` (Bogotá), nunca contra el reloj del navegador. */
function ColaDeTrabajo({dashboard, state, businessId}: {dashboard: Dashboard; state: SgsstState; businessId: string}) {
  const {t} = useTranslation()
  const {data: actionsPage} = useActions(businessId)
  const {data: solicitudes} = useSolicitudes(businessId)
  const {data: evidence} = useEvidence(businessId)
  const {data: designations} = useDesignations(businessId)
  const hoy = dashboard.hoy
  const pronto = sumarDias(hoy, DIAS_ESTA_SEMANA)
  const audit = useMemo(
    () =>
      readiness(state.assessment?.items ?? [], evidence?.items ?? [], toSoporteDesignations(designations?.items ?? [])),
    [state, evidence, designations]
  )

  const filas = useMemo(() => {
    const out: FilaCola[] = []
    const acciones = (actionsPage?.items ?? [])
      .filter((a) => a.status !== 'cumplida' && a.target_date <= pronto)
      .sort((a, b) => a.target_date.localeCompare(b.target_date))
      .slice(0, MAX_ACCIONES_EN_COLA)
    for (const accion of acciones) {
      const vencida = accion.target_date < hoy
      out.push({
        clave: `accion-${accion.id}`,
        severidad: vencida ? 'red' : 'amber',
        que: accion.description,
        detalle: accion.standard_code ? t('home.cola.estandar', {code: accion.standard_code}) : null,
        quien: accion.responsible_employee
          ? `${accion.responsible_employee.name} ${accion.responsible_employee.last_name}`
          : null,
        cuando: vencida
          ? t('home.cola.vencio', {date: formatShortDate(accion.target_date)})
          : formatShortDate(accion.target_date),
        cta: t('home.cola.abrir'),
        to: `/businesses/${businessId}/sgsst/plan?accion=${accion.id}`,
      })
    }

    const solPorVencer = (solicitudes ?? []).filter(
      (s) => s.state === 'vencida' || (s.state === 'pendiente' && s.due_date <= pronto)
    )
    if (solPorVencer.length > 0) {
      const primera = solPorVencer.reduce((min, s) => (s.due_date < min ? s.due_date : min), solPorVencer[0].due_date)
      out.push({
        clave: 'solicitudes',
        severidad: 'amber',
        que: t('dashboard.tasks.solicitudes', {count: solPorVencer.length}),
        cuando: formatShortDate(primera),
        cta: t('dashboard.tasks.toSolicitudes'),
        to: `/businesses/${businessId}/sgsst`,
      })
    }

    if (dashboard.incidentes.relojes_corriendo > 0) {
      out.push({
        clave: 'relojes-1401',
        severidad: dashboard.incidentes.relojes_vencidos > 0 ? 'red' : 'amber',
        que: t('home.cola.relojes', {count: dashboard.incidentes.relojes_corriendo}),
        cuando: dashboard.incidentes.mas_urgente ? formatShortDate(dashboard.incidentes.mas_urgente.deadline) : null,
        cta: t('home.cola.verIncidentes'),
        to: `/businesses/${businessId}/incidentes`,
      })
    }

    if (dashboard.legal.pendiente_verificar > 0) {
      out.push({
        clave: 'normas-por-verificar',
        severidad: 'lime',
        que: t('home.cola.normas', {count: dashboard.legal.pendiente_verificar}),
        cta: t('home.cola.verMatrizLegal'),
        to: `/businesses/${businessId}/sgsst/matriz-legal`,
      })
    }

    // Estándares en no_cumple sin acción viva: las tajadas de exposición menos el plan.
    const planeados = new Set((actionsPage?.items ?? []).map((a) => a.standard_code).filter(Boolean))
    const riesgosSinPlan = (state.exposure?.slices ?? []).filter((s) => !planeados.has(s.standard_code)).length
    if (riesgosSinPlan > 0) {
      out.push({
        clave: 'riesgos-sin-plan',
        severidad: 'amber',
        que: t('dashboard.tasks.riesgosSinPlan', {count: riesgosSinPlan}),
        cta: t('dashboard.tasks.toPlan'),
        to: `/businesses/${businessId}/sgsst/plan`,
      })
    }

    // El conteo es el enlace y llega con el filtro puesto: `pages/sgsst.tsx` aplica
    // `?filter=sin-soporte` al aterrizar (U19).
    if (audit.sinSoporte.length > 0) {
      out.push({
        clave: 'sin-soporte',
        severidad: 'lime',
        que: t('home.readiness.sinSoporte'),
        cta: t('home.readiness.sinSoporteCta', {count: audit.sinSoporte.length}),
        to: `/businesses/${businessId}/sgsst?filter=sin-soporte`,
      })
    }

    if (dashboard.riesgos.pendientes_calificar > 0) {
      out.push({
        clave: 'filas-sin-calificar',
        severidad: 'lime',
        que: t('home.cola.filas', {count: dashboard.riesgos.pendientes_calificar}),
        cta: t('home.cola.verMatrizRiesgos'),
        to: `/businesses/${businessId}/sgsst/matriz-riesgos`,
      })
    }
    return out
  }, [actionsPage, solicitudes, audit, state, dashboard, businessId, hoy, pronto, t])

  return (
    <div data-testid="cola-bloque">
      <Eyebrow className="mb-2.5">{t('home.cola.eyebrow')}</Eyebrow>
      <RegimeDriftBanner
        businessId={businessId}
        linkToSgsst
        frozen={!!dashboard.evaluacion.autoevaluacion_completed_at}
      />
      {filas.length === 0 ? (
        <HappyStateCard title={t('home.cola.vacioTitle')} body={t('home.cola.vacioBody')} testId="cola-vacio" />
      ) : (
        <ColaTabla filas={filas} testId="cola-tabla" />
      )}
    </div>
  )
}

function TarjetaRiesgos({dashboard, businessId}: {dashboard: Dashboard; businessId: string}) {
  const {t} = useTranslation()
  const tokens = useChartTokens()
  const navigate = useNavigate()
  const {celdas, total_calificadas: calificadas, pendientes_calificar: pendientes} = dashboard.riesgos
  const colores = [tokens['tone-lime-fg'], tokens['tone-amber-fg'], tokens['tone-orange-fg'], tokens['tone-red-fg']]
  const href = `/businesses/${businessId}/sgsst/matriz-riesgos`
  return (
    <TileCard
      eyebrow={t('home.lente.riesgosEyebrow')}
      testId="lente-riesgos"
      cta={calificadas === 0 ? undefined : {label: t('home.lente.verMatriz'), to: href, testId: 'lente-ver-matriz'}}>
      {calificadas === 0 ? (
        <TileVacio
          titulo={t('home.lente.riesgosVacio')}
          cta={{label: t('home.lente.riesgosVacioCta'), to: href}}
          testId="lente-riesgos-vacio"
        />
      ) : (
        <>
          <DonutChart
            labels={celdas.map((c) => t('home.lente.nivel', {nivel: c.interpretacion}))}
            values={celdas.map((c) => c.count)}
            colors={colores}
            totalLabel={t('home.lente.riesgosTotal')}
            onPick={() => navigate(href)}
            testId="lente-riesgos-donut"
            height={ALTO_GRAFICA}
          />
          <p className="mt-1 text-center text-[13px] text-muted-foreground" data-testid="lente-riesgos-pendientes">
            {pendientes > 0
              ? t('home.lente.riesgosPendientes', {count: pendientes})
              : t('home.lente.riesgosTodasCalificadas')}
          </p>
        </>
      )}
    </TileCard>
  )
}

function TarjetaLegal({dashboard, businessId}: {dashboard: Dashboard; businessId: string}) {
  const {t} = useTranslation()
  const tokens = useChartTokens()
  const navigate = useNavigate()
  const {legal} = dashboard
  const colores = [tokens.primary, tokens['tone-red-fg'], tokens['muted-foreground'], tokens['tone-amber-fg']]
  const href = `/businesses/${businessId}/sgsst/matriz-legal`
  return (
    <TileCard
      eyebrow={t('home.lente.legalEyebrow')}
      testId="lente-legal"
      cta={legal.total === 0 ? undefined : {label: t('home.lente.verLegal'), to: href, testId: 'lente-ver-legal'}}>
      {legal.total === 0 ? (
        <TileVacio
          titulo={t('home.lente.legalVacio')}
          cta={{label: t('home.lente.legalVacioCta'), to: href}}
          testId="lente-legal-vacio"
        />
      ) : (
        <>
          <DonutChart
            labels={[
              t('matrizLegal.estado.cumple'),
              t('matrizLegal.estado.no_cumple'),
              t('matrizLegal.estado.no_aplica'),
              t('matrizLegal.estado.pendiente_verificar'),
            ]}
            values={[legal.cumple, legal.no_cumple, legal.no_aplica, legal.pendiente_verificar]}
            colors={colores}
            totalLabel={t('home.lente.legalTotal')}
            onPick={() => navigate(href)}
            testId="lente-legal-donut"
            height={ALTO_GRAFICA}
          />
          <p className="mt-1 text-center text-[13px] text-muted-foreground" data-testid="lente-legal-pendientes">
            {legal.pendiente_verificar > 0
              ? t('home.lente.legalPendientes', {count: legal.pendiente_verificar})
              : t('home.lente.legalAlDia')}
          </p>
        </>
      )}
    </TileCard>
  )
}

function TarjetaPlanTrabajo({plan, businessId}: {plan: PlanTrabajo | null; businessId: string}) {
  const {t} = useTranslation()
  const tokens = useChartTokens()
  const href = `/businesses/${businessId}/plan-trabajo`
  return (
    <TileCard
      eyebrow={t('home.lente.planTrabajoEyebrow')}
      testId="lente-plan-trabajo"
      cta={plan ? {label: t('home.lente.verPlanTrabajo'), to: href} : undefined}>
      {plan ? (
        <>
          <RadialGauge
            value={plan.avance_pct}
            label={t('home.lente.planTrabajoAvance', {year: plan.period_year})}
            color={tokens['chart-hacer']}
            testId="lente-plan-trabajo-gauge"
            height={ALTO_GRAFICA + 20}
          />
          <p className="mt-1 text-center text-[13px] text-muted-foreground">
            <CountUp value={plan.actividades.length} /> {t('home.lente.planTrabajoActividades')}
          </p>
        </>
      ) : (
        <TileVacio
          titulo={t('home.lente.planTrabajoVacio')}
          cta={{label: t('home.lente.planTrabajoVacioCta'), to: href}}
          testId="lente-plan-trabajo-vacio"
        />
      )}
    </TileCard>
  )
}

/** La lente del responsable. Con dos o más frentes vacíos se funden en «Para arrancar»; uno
 * solo conserva su tarjeta. */
export function ResumenOperativo({
  dashboard,
  state,
  businessId,
  ruta,
}: {
  dashboard: Dashboard
  state: SgsstState
  businessId: string
  ruta: Ruta
}) {
  const {data: estadoPlanTrabajo, isPending: planTrabajoCargando} = usePlanTrabajo(businessId)
  const planTrabajo = estadoPlanTrabajo?.plan ?? null
  const riesgosVacio = dashboard.riesgos.total_calificadas === 0
  const legalVacio = dashboard.legal.total === 0
  const planTrabajoVacio = planTrabajo === null
  const vacios = Number(riesgosVacio) + Number(legalVacio) + Number(planTrabajoVacio)
  const colapsa = vacios >= 2
  return (
    <div className="space-y-4" data-testid="resumen-operativo">
      <ColaDeTrabajo dashboard={dashboard} state={state} businessId={businessId} />
      {/* El colapso no se decide con el plan de trabajo a medio llegar: la fila parpadearía. */}
      {planTrabajoCargando ? (
        <Skeleton className="h-56 w-full" />
      ) : (
        <div className="grid gap-4 sm:grid-cols-3 [&>*]:min-w-0">
          {(!riesgosVacio || !colapsa) && <TarjetaRiesgos dashboard={dashboard} businessId={businessId} />}
          {(!legalVacio || !colapsa) && <TarjetaLegal dashboard={dashboard} businessId={businessId} />}
          {(!planTrabajoVacio || !colapsa) && <TarjetaPlanTrabajo plan={planTrabajo} businessId={businessId} />}
          {colapsa && <EstadoDeArranque ruta={ruta} businessId={businessId} className={COLUMNAS_ARRANQUE[vacios]} />}
        </div>
      )}
    </div>
  )
}
